import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class PaymentService {
  private serverUrl = String(environment.serverUrl || '').replace(/\/\/+$|\/+$/g, '');

  constructor(private http: HttpClient) { }

  // ========================================
  // STRIPE
  // ========================================

  createSetupIntent(email: string) {
    return this.http.post<{ client_secret: string }>(`${this.serverUrl}/setup-intent`, { email });
  }

  savePaymentMethod(email: string, paymentMethodId: string) {
    return this.http.post<{ paymentMethodId: string }>(`${this.serverUrl}/save-payment-method`, { email, paymentMethodId });
  }

  retrievePaymentMethod(paymentMethodId: string) {
    return this.http.post<any>(`${this.serverUrl}/retrieve-payment-method`, { paymentMethodId });
  }

  checkCardExistsStripe(email: string, last4: string) {
    return this.http.post<{ exists: boolean }>(`${this.serverUrl}/check-card-exists`, { email, last4 });
  }

  payWithStripe(amount: number, currency: string, paymentMethodId: string, customerId: string) {
    return this.http.post<{ paymentIntent: any }>(`${this.serverUrl}/pay/stripe`, { amount, currency, paymentMethodId, customerId });
  }

  processPaymentWithCardId(email: string, amount: number, cardId: string): Observable<any> {
    return this.http.post('/api/process-payment', { email, amount, cardId });
  }

  processRidePayment(paymentData: {
    email: string; amount: number; currency: string;
    paymentMethodId: string; driverId: string; rideId: string;
    driverAmount: number; companyAmount: number;
  }): Observable<any> {
    return this.http.post(`${this.serverUrl}/process-ride-payment`, paymentData);
  }

  createPaymentIntent(paymentData: {
    email: string; amount: number; currency: string;
    paymentMethodId: string; description: string;
  }): Observable<any> {
    return this.http.post(`${this.serverUrl}/create-payment-intent`, paymentData);
  }

  // ========================================
  // PAYSTACK USSD
  // ========================================

  /** Initialize a Paystack USSD payment. Returns an authorization_url to open. */
  initiateUSSD(email: string, amount: number, currency: string = 'NGN'): Observable<any> {
    return this.http.post(`${this.serverUrl}/paystack/ussd`, { email, amount, currency });
  }

  // ========================================
  // PAYSTACK BANK TRANSFER
  // ========================================

  /** Initialize a Paystack bank transfer. Returns an authorization_url with bank details. */
  initiateBankTransfer(email: string, amount: number, currency: string = 'NGN'): Observable<any> {
    return this.http.post(`${this.serverUrl}/paystack/bank-transfer`, { email, amount, currency });
  }

  // ========================================
  // PAYSTACK STATUS CHECK
  // ========================================

  /** Check the status of a Paystack transaction by reference. */
  checkPaystackStatus(reference: string): Observable<any> {
    return this.http.post(`${this.serverUrl}/paystack/check-status`, { reference });
  }

  /** Verify a Paystack transaction by reference (GET). */
  verifyPaystackTransaction(reference: string): Observable<any> {
    return this.http.get(`${this.serverUrl}/paystack/verify/${reference}`);
  }

  // ========================================
  // COINBASE COMMERCE (CRYPTO)
  // ========================================

  /** Create a Coinbase Commerce crypto charge. Returns hosted_url and wallet addresses. */
  createCryptoCharge(amount: number, currency: string = 'USD', userId: string): Observable<any> {
    return this.http.post(`${this.serverUrl}/crypto/create-charge`, {
      amount, currency, userId,
      name: 'Wallet Top-Up',
      description: 'Add funds via cryptocurrency'
    });
  }

  /** Check a Coinbase Commerce charge status. */
  checkCryptoCharge(chargeId: string): Observable<any> {
    return this.http.get(`${this.serverUrl}/crypto/check-charge/${chargeId}`);
  }
}
